import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { debounce } from 'lodash';
import {
  adminFiltersAtom,
  adminFilterOptionsQueryAtom,
  adminAnalysesQueryAtom,
  pairsAtom,
} from 'r/atoms';
import { MultiSelectComboBox } from 'r/ui';
import type { MultiSelectOption } from 'r/ui/MultiSelectComboBox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface AdminFiltersProps {
  isFetching: boolean;
}

const TIMEFRAME_OPTIONS = [
  { value: '1d', label: '1 Day' },
  { value: '1w', label: '1 Week' },
  { value: '2w', label: '2 Weeks' },
  { value: '1M', label: '1 Month' },
];

const PERFORMANCE_FILTER_OPTIONS = [
  { value: '>5', label: '> 5%' },
  { value: '>10', label: '> 10%' },
  { value: '>50', label: '> 50%' },
  { value: '<-5', label: '< -5%' },
  { value: '<-10', label: '< -10%' },
  { value: '<-50', label: '< -50%' },
];

export default function AdminFilters({ isFetching }: AdminFiltersProps) {
  const [filters, setFilters] = useAtom(adminFiltersAtom);
  const { authors, symbols } = useAtomValue(adminFilterOptionsQueryAtom);
  const mainQuery = useAtomValue(adminAnalysesQueryAtom);
  const pairsQuery = useAtomValue(pairsAtom);
  const pairs = pairsQuery.data || [];
  const total = mainQuery.data?.total || 0;

  const [localSearch, setLocalSearch] = useState(filters.search);

  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setFilters((prev) => ({ ...prev, search: value.trim(), page: 0 }));
    }, 750),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
    debouncedSetSearch(value);
  };

  const handleSearchClear = () => {
    debouncedSetSearch.cancel();
    setLocalSearch('');
    setFilters((prev) => ({ ...prev, search: '', page: 0 }));
  };

  const handleAuthorChange = (author: string) => {
    setFilters((prev) => ({
      ...prev,
      author: author === 'all' ? null : author,
      page: 0,
    }));
  };

  const pairsWithLogos: MultiSelectOption[] = useMemo(() => {
    const pairsMap = new Map(pairs.map((p) => [p.symbol, p.logoUrl]));

    return symbols
      .map((symbol) => ({
        id: symbol.value,
        label: symbol.value,
        icon: pairsMap.get(symbol.value) || null,
        sublabel: symbol.count > 1 ? `(${symbol.count})` : undefined,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [symbols, pairs]);

  const selectedPairOption: MultiSelectOption[] = useMemo(() => {
    if (!filters.symbol) return [];
    const pair = pairsWithLogos.find((p) => p.id === filters.symbol);
    return pair ? [pair] : [];
  }, [filters.symbol, pairsWithLogos]);

  const handlePairChange = useCallback(
    (selected: MultiSelectOption[]) => {
      setFilters((prev) => ({
        ...prev,
        symbol: selected.length > 0 ? selected[0].id : null,
        page: 0,
      }));
    },
    [setFilters],
  );

  const handleTimeframeChange = (timeframe: string) => {
    setFilters((prev) => ({
      ...prev,
      timeframe: timeframe === 'none' ? null : timeframe,
      performanceFilter: null,
      page: 0,
    }));
  };

  const handlePerformanceFilterChange = (performanceFilter: string) => {
    setFilters((prev) => ({
      ...prev,
      performanceFilter:
        performanceFilter === 'none' ? null : performanceFilter,
      page: 0,
    }));
  };

  const handleShowApiAnalysesOnlyChange = (checked: boolean) => {
    setFilters((prev) => ({
      ...prev,
      showApiAnalysesOnly: checked,
      page: 0,
    }));
  };

  return (
    <div className="bg-background p-4 rounded-lg mb-4 space-y-4">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 space-y-2">
          <Label htmlFor="search">Search analyses</Label>
          <div className="relative">
            <Input
              id="search"
              aria-label="Search by tweet text or author"
              placeholder="Search by tweet text or author..."
              value={localSearch}
              onChange={(e) => handleSearchChange(e.target.value)}
              disabled={isFetching}
              className="w-full"
            />
            {localSearch && (
              <button
                onClick={handleSearchClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className="w-full md:w-64 space-y-2">
          <Label>Author</Label>
          <Select
            value={filters.author || 'all'}
            onValueChange={handleAuthorChange}
            disabled={isFetching}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All Authors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Authors</SelectItem>
              {authors.map((author) => (
                <SelectItem key={author.value} value={author.value}>
                  {author.value} {author.count > 1 && `(${author.count})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-full md:w-64 space-y-2">
          <MultiSelectComboBox
            label="Pair"
            placeholder="All Pairs"
            searchPlaceholder="Search pairs..."
            selectedItems={selectedPairOption}
            items={pairsWithLogos}
            onSelectionChange={handlePairChange}
            isDisabled={isFetching}
            fuseKeys={['label']}
            single
          />
        </div>

        <div className="w-full md:w-40 space-y-2">
          <Label>Timeframe</Label>
          <Select
            value={filters.timeframe || 'none'}
            onValueChange={handleTimeframeChange}
            disabled={isFetching}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All Timeframes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">All Timeframes</SelectItem>
              {TIMEFRAME_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-full md:w-40 space-y-2">
          <Label>Performance</Label>
          <Select
            key={filters.timeframe || 'no-tf'}
            value={filters.performanceFilter || 'none'}
            onValueChange={handlePerformanceFilterChange}
            disabled={isFetching || !filters.timeframe}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All Performance" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">All Performance</SelectItem>
              {PERFORMANCE_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* API Analyses Only Checkbox */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="api-only"
          checked={filters.showApiAnalysesOnly}
          onCheckedChange={handleShowApiAnalysesOnlyChange}
          disabled={isFetching}
        />
        <Label
          htmlFor="api-only"
          className="text-sm font-normal cursor-pointer"
        >
          Show api analyses only
        </Label>
      </div>

      <div className="text-sm text-secondary">
        Find {total} analyses
        {filters.author && ` for author: ${filters.author}`}
        {filters.symbol && ` with pair: ${filters.symbol}`}
        {filters.showApiAnalysesOnly && ' (API only)'}
      </div>
    </div>
  );
}
